import { useEffect } from 'react';
import { AlertTriangle, Check, Info, X, XCircle } from 'lucide-react';
import { useToastStore, type Toast as ToastModel, type ToastVariant } from '@/stores/toast.store';
import { cn } from '@/utils/cn';

const ICONS: Record<ToastVariant, typeof Check> = {
  success: Check,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const TONES: Record<ToastVariant, string> = {
  success: 'text-success',
  error: 'text-danger',
  warning: 'text-warning',
  info: 'text-info',
};

function ToastItem({ toast }: { toast: ToastModel }) {
  const dismiss = useToastStore((state) => state.dismiss);
  const Icon = ICONS[toast.variant];

  useEffect(() => {
    if (toast.duration <= 0) return undefined;
    const timer = window.setTimeout(() => dismiss(toast.id), toast.duration);
    return () => window.clearTimeout(timer);
  }, [toast.id, toast.duration, dismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-auto flex w-full animate-slide-up items-start gap-3 rounded-md border border-border bg-surface-raised p-3 shadow-lg"
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', TONES[toast.variant])} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-fg">{toast.title}</p>
        {toast.description ? (
          <p className="mt-0.5 break-words text-xs text-subtle">{toast.description}</p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        className="rounded p-0.5 text-subtle transition-colors hover:text-fg"
        aria-label="Dismiss notification"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Fixed viewport for toasts. Mounted once, near the root. */
export function ToastViewport() {
  const toasts = useToastStore((state) => state.toasts);
  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2">
      {toasts.map((item) => (
        <ToastItem key={item.id} toast={item} />
      ))}
    </div>
  );
}
